import React, { useState, useEffect, useCallback } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COOKIE_CONSENT_KEY = 'cookie_consent';

export const CookieBanner = ({ isOpen, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Check initial consent on mount
    useEffect(() => {
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!consent) {
            // Show banner if no consent stored
            const timer = setTimeout(() => {
                setIsVisible(true);
                setIsAnimating(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Handle manual open from footer (when isOpen prop changes)
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            const timer = setTimeout(() => setIsAnimating(true), 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

	const handleConsent = useCallback((accepted) => {
		localStorage.setItem(COOKIE_CONSENT_KEY, accepted ? 'accepted' : 'denied');
		// 通知其他组件（例如移动端悬浮 CTA）及时更新位置/状态
		try {
			window.dispatchEvent(new CustomEvent('cookie-consent-changed'));
		} catch (_) {
			// ignore
		}
        setIsAnimating(false);
        const timer = setTimeout(() => {
            setIsVisible(false);
            if (onClose) onClose();
        }, 300);
        return () => clearTimeout(timer);
    }, [onClose]);

    const handleClose = useCallback(() => {
        setIsAnimating(false);
        const timer = setTimeout(() => {
            setIsVisible(false);
            if (onClose) onClose();
        }, 300);
        return () => clearTimeout(timer);
    }, [onClose]);

    if (!isVisible) return null;

    return (
        <div
            className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ease-out ${
                isAnimating
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-full opacity-0'
            }`}
        >
            <div className="section-container">
                <div className="bg-card rounded-2xl shadow-elevated border border-border p-4 md:p-6 max-w-4xl mx-auto relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {/* Icon and Text */}
                        <div className="flex items-start gap-3 flex-1">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Cookie className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-semibold text-foreground text-sm mb-1">
                                    Cookie-Einstellungen
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Wir verwenden Cookies, um Ihr Erlebnis zu verbessern.
                                    Durch die Nutzung unserer Website stimmen Sie unserer
                                    Cookie-Richtlinie zu.
                                </p>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                onClick={() => handleConsent(false)}
                                className="flex-1 sm:flex-none text-sm"
                            >
                                Ablehnen
                            </Button>
                            <Button
                                onClick={() => handleConsent(true)}
                                className="flex-1 sm:flex-none text-sm bg-primary hover:bg-primary/90 text-primary-foreground"
                            >
                                Akzeptieren
                            </Button>
                        </div>

                        {/* Close Button (for when opened from settings) */}
                        {isOpen && (
                            <button
                                onClick={handleClose}
                                className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
                                aria-label="Schließen"
                            >
                                <X className="h-4 w-4 text-muted-foreground" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Hook to manage cookie consent state
export const useCookieConsent = () => {
    const [consent, setConsent] = useState(() => {
        // Initialize from localStorage synchronously
        if (typeof window !== 'undefined') {
            return localStorage.getItem(COOKIE_CONSENT_KEY);
        }
        return null;
    });

    const resetConsent = useCallback(() => {
        localStorage.removeItem(COOKIE_CONSENT_KEY);
        setConsent(null);
    }, []);

    return { consent, resetConsent };
};
